import { Injectable } from '@angular/core';
import { ApiService } from '@app/services/api/api.service';
import { FlashService } from '@app/services/flash.service';
import { WebsocketService } from '@app/services/websocket.service';
import * as DepartamentsBusinessColorActions from '@core/actions/departaments-business-color.actions';
import * as PortActions from "@core/actions/port.actions";
import {
	EportShippingMarksXlsx,
	ExportDiscrepanciesDownload,
	ExportDiscrepanciesDownloadSuccess,
	ExportDiscrepanciesSuccess,
	ExportFollowDiscrepancies,
	ExportFollowDiscrepanciesSuccess,
	ExportProformaList,
	ExportPurchaseSummary,
	ExportPurchaseSummarySuplier,
	GetUploadAuxiliar,
	GetUploadAuxiliarFail,
	GetUploadLink,
	GetUploadLinkFail,
	GetUploadLinkNew,
	GetUploadLinkNewDetailProformas,
	GetUploadLinkPort,
	GetUploadLinkShippingTimes,
	Load,
	LoadShippingMarksXlsx,
	LoadShippingMarksXlsxFail,
	ReportsActionTypes,
	UploadFile,
	UploadFileFail,
	UploadFileNew,
	UploadFileNewDetailProformas,
	UploadFileNewDetailProformasSuccess,
	UploadFileNewFail,
	UploadFileNewSuccess,
	UploadFilePort,
	UploadFilePortSuccess,
	UploadFileShippingMarksXlsx,
	UploadFileShippingMarksXlsxFail,
	UploadFileShippingMarksXlsxProcessFail,
	UploadFileShippingMarksXlsxProcessSuccess,
	UploadFileShippingMarksXlsxSuccess,
	UploadFileShippingTimes,
	UploadFileShippingTimesSuccess,
	UploadFileSuccess,
	UploadProcessFail,
	UploadProcessNewSuccess,
	UploadProcessSuccess,
	UploadProcessUpdate
} from '@core/actions/reports.actions';

import { Actions, Effect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, exhaustMap, map, switchMap, tap } from 'rxjs/operators';

@Injectable()
export class ReportsEffects {
	@Effect({ dispatch: false })
	ExportPurchaseSummarySuplier$ = this.actions$.pipe(
		ofType<ExportPurchaseSummarySuplier>(ReportsActionTypes.ExportPurchaseSummarySuplier),
		map(action => [action.FollowId, action.ProviderId, action.filename, action.types, action.OriginId, action.RegionalId, action.enableShipment, action.exportDiscrepanciesId]),
		map(([FollowId, ProviderId, filename, types, OriginId, RegionalId, enableShipment, exportDiscrepanciesId]) => {
			this.api.ExportPurchaseSummarySuplier(FollowId, ProviderId, filename, types, OriginId, RegionalId, enableShipment, exportDiscrepanciesId);

		})
	);

	@Effect({ dispatch: false })
	exportPurchaseSummary$ = this.actions$.pipe(
		ofType<ExportPurchaseSummary>(ReportsActionTypes.ExportPurchaseSummary),
		map(action => [action.FollowId, action.ProviderId, action.filename, action.types, action.OriginId, action.RegionalId, action.enableShipment, action.exportDiscrepanciesId]),
		map(([FollowId, ProviderId, filename, types, OriginId, RegionalId, enableShipment, exportDiscrepanciesId]) => {
			this.api.exportPurchaseSummary(FollowId, ProviderId, filename, types, OriginId, RegionalId, enableShipment, exportDiscrepanciesId);

		})
	);

	@Effect({ dispatch: false })
	exportFollowDiscrepancies = this.actions$.pipe(
		ofType<ExportFollowDiscrepancies>(ReportsActionTypes.ExportFollowDiscrepancies),
		map(action => [action.FollowId, action.filename, action.filter]),
		map(([FollowId, filename, filter]) => {
			this.api.exportFollowDiscrepancies(FollowId, filename, filter);

		})
	);

	@Effect({ dispatch: false })
	exportFollowDiscrepanciesSuccess = this.actions$.pipe(
		ofType<ExportFollowDiscrepanciesSuccess>(ReportsActionTypes.ExportFollowDiscrepanciesSuccess),
		map(action => {
			this.api.downloadDiscrepanciesZip(action.data.downloadUrl, action.data.name)
		})
	);

	@Effect({ dispatch: false })
	exportProformaList = this.actions$.pipe(
		ofType<ExportProformaList>(ReportsActionTypes.ExportProformaList),
		map(action => [action.FollowId, action.CountryId, action.filename, action.user]),
		map(([FollowId, CountryId, filename, user]) => {
			this.api.exportProformaList(FollowId, CountryId, filename, user);
		})
	);

	@Effect({ dispatch: false })
	eportShippingMarksXlsx = this.actions$.pipe(
		ofType<EportShippingMarksXlsx>(ReportsActionTypes.EportShippingMarksXlsx),
		map(action => action.businessUnits),
		map((businessUnits) => {
			this.api.eportShippingMarksXlsx(businessUnits);
		})
	);

	@Effect()
	getUploadLink$: Observable<Action> = this.actions$.pipe(
		ofType<GetUploadLink>(ReportsActionTypes.GetUploadLink),
		map(action => [action.file, action.data]),
		switchMap(([file, data]) => {
			return this.api.getProformasUploadLink(file, data).pipe(
				map((record: any) => {
					return new UploadFile(record.signedUrl, record.id, file, data);
				}),
				catchError(e => of(new GetUploadLinkFail()))
			);
		})
	);

	@Effect()
	getUploadLinkNew$: Observable<Action> = this.actions$.pipe(
		ofType<GetUploadLinkNew>(ReportsActionTypes.GetUploadLinkNew),
		map(action => [action.file, action.forceUpload, action.FollowId]),
		switchMap(([file, forceUpload, FollowId]) => {
			return this.api.getProformasUploadLink(file).pipe(
				map((record: any) => {
					return new UploadFileNew(record.signedUrl, record.id, file, forceUpload, FollowId);
				}),
				catchError(e => of(new GetUploadLinkFail()))
			);
		})
	);

	@Effect()
	getUploadLinkNewDetailProformas$: Observable<Action> = this.actions$.pipe(
		ofType<GetUploadLinkNewDetailProformas>(ReportsActionTypes.GetUploadLinkNewDetailProformas),
		map(action => [action.file, action.forceUpload, action.FollowId]),
		switchMap(([file, forceUpload, FollowId]) => {
			return this.api.getDetailProformasUploadLink(file, FollowId).pipe(
				map((record: any) => {
					return new UploadFileNewDetailProformas(record.signedUrl, record.id, file, forceUpload, FollowId);
				}),
				catchError(e => of(new GetUploadLinkFail()))
			);
		})
	);

	@Effect()
	getUploadLinkPort$: Observable<Action> = this.actions$.pipe(
		ofType<GetUploadLinkPort>(ReportsActionTypes.GetUploadLinkPort),
		map(action => [action.file]),
		switchMap(([file, forceUpload, FollowId]) => {
			return this.api.getPortUploadLink(file).pipe(
				map((record: any) => {
					return new UploadFilePort(record.signedUrl, record.id, file);
				}),
				catchError(e => of(new GetUploadLinkFail()))
			);
		})
	);

	@Effect()
	getUploadLinkShippingTimes$: Observable<Action> = this.actions$.pipe(
		ofType<GetUploadLinkShippingTimes>(ReportsActionTypes.GetUploadLinkShippingTimes),
		map(action => [action.file]),
		switchMap(([file, forceUpload, FollowId]) => {
			return this.api.getShippingTimeUploadLink(file).pipe(
				map((record: any) => {
					return new UploadFileShippingTimes(record.signedUrl, record.id, file);
				}),
				catchError(e => of(new GetUploadLinkFail()))
			);
		})
	);

	@Effect()
	getUploadAuxiliar$: Observable<Action> = this.actions$.pipe(
		ofType<GetUploadAuxiliar>(ReportsActionTypes.GetUploadAuxiliar),
		map(action => [action.file, action.followId]),
		switchMap(([file, followId]) => {
			return this.api.getAuxiliarShippingMarksUploadLink(file, followId).pipe(
				map((record: any) => {
					return new UploadFile(record.signedUrl, record.id, file, 'shippingMarks');
				}),
				catchError(e => of(new GetUploadAuxiliarFail()))
			);
		})
	);

	@Effect()
	uploadFile$: Observable<Action> = this.actions$.pipe(
		ofType<UploadFile>(ReportsActionTypes.UploadFile),
		map(action => [action.uploadUrl, action.FileId, action.file, action.mode]),
		switchMap(([uploadUrl, FileId, file, mode]) => {
			return this.api.uploadFile(uploadUrl, file).pipe(
				map(record => {
					return new UploadFileSuccess(FileId, mode);
				}),
				catchError(e => of(new UploadFileFail()))
			);
		})
	);

	@Effect()
	uploadFileShippingMarksXlsx$: Observable<Action> = this.actions$.pipe(
		ofType<UploadFileShippingMarksXlsx>(ReportsActionTypes.UploadFileShippingMarksXlsx),
		map(action => [action.uploadUrl, action.FileId, action.file]),
		switchMap(([uploadUrl, FileId, file]) => {
			return this.api.uploadFile(uploadUrl, file).pipe(
				concatMap(record => {
					this.ws.registerFileListener(FileId)
					return [new UploadFileShippingMarksXlsxSuccess(FileId)];
				}),
				catchError(e => of(new UploadFileShippingMarksXlsxFail()))
			);
		})
	);

	@Effect()
	uploadFileShippingMarksXlsxProcessFail$ = this.actions$.pipe(
		ofType<UploadFileShippingMarksXlsxProcessFail>(ReportsActionTypes.UploadFileShippingMarksXlsxProcessFail),
		map((actions: any) => {
			this.fs.emit({
				type: 'danger',
				message: actions.data.message
			});
			return new Load()
		})
	);

	@Effect()
	uploadFileNew$: Observable<Action> = this.actions$.pipe(
		ofType<UploadFileNew>(ReportsActionTypes.UploadFileNew),
		map(action => [action.uploadUrl, action.FileId, action.file, action.forceUpload, action.FollowId]),
		switchMap(([uploadUrl, FileId, file, forceUpload, FollowId]) => {
			return this.api.uploadFile(uploadUrl, file).pipe(
				map(record => {
					return new UploadFileNewSuccess(file, FileId, forceUpload, FollowId);
				}),
				catchError(e => of(new UploadFileNewFail()))
			);
		})
	);

	@Effect()
	uploadFileNewDetailProformas$: Observable<Action> = this.actions$.pipe(
		ofType<UploadFileNewDetailProformas>(ReportsActionTypes.UploadFileNewDetailProformas),
		map(action => [action.uploadUrl, action.FileId, action.file, action.forceUpload, action.FollowId]),
		switchMap(([uploadUrl, FileId, file, forceUpload, FollowId]) => {
			return this.api.uploadFile(uploadUrl, file).pipe(
				map(record => {
					return new UploadFileNewDetailProformasSuccess(file, FileId, forceUpload, FollowId);
				}),
				catchError(e => of(new UploadFileNewFail()))
			);
		})
	);

	@Effect()
	uploadFilePort$: Observable<Action> = this.actions$.pipe(
		ofType<UploadFilePort>(ReportsActionTypes.UploadFilePort),
		map(action => [action.uploadUrl, action.FileId, action.file]),
		switchMap(([uploadUrl, FileId, file]) => {
			return this.api.uploadFile(uploadUrl, file).pipe(
				map(record => {
					return new UploadFilePortSuccess(file, FileId);
				}),
				catchError(e => of(new UploadFileNewFail()))
			);
		})
	);

	@Effect()
	uploadFileShippingTimes$: Observable<Action> = this.actions$.pipe(
		ofType<UploadFileShippingTimes>(ReportsActionTypes.UploadFileShippingTimes),
		map(action => [action.uploadUrl, action.FileId, action.file]),
		switchMap(([uploadUrl, FileId, file]) => {
			return this.api.uploadFile(uploadUrl, file).pipe(
				map(record => {
					return new UploadFileShippingTimesSuccess(file, FileId);
				}),
				catchError(e => of(new UploadFileNewFail()))
			);
		})
	);

	@Effect()
	uploadFileSuccess$ = this.actions$.pipe(
		ofType<UploadFileSuccess>(ReportsActionTypes.UploadFileSuccess),
		map(action => ([action.FileId, action.mode])),
		switchMap(([FileId, mode]) => {
			let proccess = 'Proformas'
			let metadata = { }

			if(mode && mode === 'shippingMarks'){
				metadata = {
					processType: mode
				}
				this.fs.emit({
					type: 'success',
					message: 'Maestra Round Stickers actualizado'
				});
			}else if(mode && mode && mode.mode === 'Sustainability'){
				metadata = {
					processType: mode.mode,
					parentId: mode.ParentId
				}
			}else{
				metadata = {
					processType: proccess
				}
			}

			return this.api
				.notifyFileUpload(FileId, metadata)
				.pipe(
					map(record => {
						return new UploadProcessUpdate({
							status: 'pending',
							message: 'Procesando proformas...'
						});
					}),
					catchError(e => of(new UploadProcessFail()))
				);
		})
	);

	@Effect()
	uploadFileShippingMarksXlsxSuccess$ = this.actions$.pipe(
		ofType<UploadFileShippingMarksXlsxSuccess>(ReportsActionTypes.UploadFileShippingMarksXlsxSuccess),
		map(action => ([action.FileId])),
		switchMap(([FileId]) => {
			let proccess = 'ShippingMarkXlsx'
			let metadata = {
				processType: proccess
			}

			return this.api
				.notifyFileUpload(FileId, metadata)
				.pipe(
					map(record => {
						return new UploadProcessUpdate({
							status: 'pending',
							message: 'Procesando Shipping marks...'
						});
					}),
					catchError(e => of(new UploadProcessFail()))
				);
		})
	);

	@Effect()
	uploadFileNewSuccess$ = this.actions$.pipe(
		ofType<UploadFileNewSuccess>(ReportsActionTypes.UploadFileNewSuccess),
		map(action => ([action.file, action.FileId, action.forceUpload, action.FollowId])),
		switchMap(([file, FileId, forceUpload, FollowId]) => {
			const data = {
				file: {
					id: FileId,
					lastModified: file.lastModified,
					size: file.size,
					type: file.type,
					webkitRelativePath: file.webkitRelativePath,
					name: file.name
				},
				forceUpload,
				FollowId
			}
			return this.api
				.uploadFileProformas(data)
				.pipe(
					map(record => {
						return new UploadProcessUpdate({
							status: 'pending',
							message: 'Procesando proformas...'
						});
					}),
					catchError(e => of(new UploadProcessFail()))
				);
		})
	);

	@Effect()
	uploadFileNewDetailProformasSuccess$ = this.actions$.pipe(
		ofType<UploadFileNewDetailProformasSuccess>(ReportsActionTypes.UploadFileNewDetailProformasSuccess),
		map(action => ([action.file, action.FileId, action.forceUpload, action.FollowId])),
		switchMap(([file, FileId, forceUpload, FollowId]) => {
			const data = {
				file: {
					id: FileId,
					lastModified: file.lastModified,
					size: file.size,
					type: file.type,
					webkitRelativePath: file.webkitRelativePath,
					name: file.name
				},
				forceUpload,
				FollowId
			}
			return this.api
				.uploadFileDetailProformas(data)
				.pipe(
					map(record => {
						return new UploadProcessUpdate({
							status: 'pending',
							message: 'Procesando detalle proformas...'
						});
					}),
					catchError(e => of(new UploadProcessFail()))
				);
		})
	);

	@Effect()
	uploadFilePortSuccess$ = this.actions$.pipe(
		ofType<UploadFilePortSuccess>(ReportsActionTypes.UploadFilePortSuccess),
		map(action => ([action.file, action.FileId])),
		switchMap(([file, FileId]) => {
			return this.api
				.uploadFilePort(FileId)
				.pipe(
					map(record => {
						return new PortActions.UploadProcessPending();
					}),
					catchError(e => of(new UploadProcessFail()))
				);
		})
	);

	@Effect()
	uploadFileShippingTimesSuccess$ = this.actions$.pipe(
		ofType<UploadFileShippingTimesSuccess>(ReportsActionTypes.UploadFileShippingTimesSuccess),
		map(action => ([action.file, action.FileId])),
		switchMap(([file, FileId]) => {
			const data = {
				file: {
					id: FileId,
					lastModified: file.lastModified,
					size: file.size,
					type: file.type,
					webkitRelativePath: file.webkitRelativePath,
					name: file.name
				},
			}
			return this.api
				.uploadFileShippingTimes(data)
				.pipe(
					map(record => {
						return new UploadProcessUpdate({
							status: 'pending',
							message: 'Procesando detalle proformas...'
						});
					}),
					catchError(e => of(new UploadProcessFail()))
				);
		})
	);

	@Effect({ dispatch: false })
	uploadProcessSuccess$ = this.actions$.pipe(
		ofType<UploadProcessSuccess>(ReportsActionTypes.UploadProcessSuccess),
		map(action => {
			this.fs.emit({ type: 'success', message: 'Proformas actualizadas' });
			// return new Load();
		})
	);

	@Effect()
  uploadFileShippingMarksXlsxProcessSuccess$ = this.actions$.pipe(
    ofType<UploadFileShippingMarksXlsxProcessSuccess>(ReportsActionTypes.UploadFileShippingMarksXlsxProcessSuccess),
    map(action => {
      this.fs.emit({ type: 'success', message: 'Colores actualizados' });
      return new DepartamentsBusinessColorActions.Load()
    })
  );

	@Effect({ dispatch: false })
	uploadProcessNewSuccess$ = this.actions$.pipe(
		ofType<UploadProcessNewSuccess>(ReportsActionTypes.UploadProcessNewSuccess),
		map(action => {
		})
	);

	@Effect()
	uploadProcessFail$ = this.actions$.pipe(
		ofType<UploadProcessFail>(ReportsActionTypes.UploadProcessFail),
		map(() => {
			this.fs.emit({
				type: 'danger',
				message: 'Error al actualizar proformas'
			});
			// return new Load();
		})
	);

	@Effect()
	exportDiscrepanciesSuccess$ = this.actions$.pipe(
    	ofType<ExportDiscrepanciesSuccess>(ReportsActionTypes.ExportDiscrepanciesSuccess),
		map(action => {
		return new ExportDiscrepanciesDownload(action.data);
		})
  	);

	@Effect({ dispatch: false })
	exportDiscrepanciesDownload$ = this.actions$.pipe(
    	ofType<ExportDiscrepanciesDownload>(ReportsActionTypes.ExportDiscrepanciesDownload),
    	map(action => [action.data.downloadUrl, action.data.name]),
    	tap(([downloadUrl, name]) => {
        		this.api.downloadDiscrepancies(downloadUrl, name);
    	})
	);

	@Effect({ dispatch: false })
	exportDiscrepanciesDownloadSuccess$ = this.actions$.pipe(
		ofType<ExportDiscrepanciesDownloadSuccess>(
			ReportsActionTypes.ExportDiscrepanciesDownloadSuccess
		),
		map(action => {
			this.fs.emit({ type: 'success', message: 'Discrepancia descargada!' });
		})
	);

	@Effect()
  getShippingMarkMasterLink$ = this.actions$.pipe(
    ofType<LoadShippingMarksXlsx>(ReportsActionTypes.LoadShippingMarksXlsx),
    map(action => [action.file]),
    exhaustMap(([file]) =>
      this.api.getShippingMarksUploadLink(file).pipe(
        concatMap((link: any) => {
					return [new UploadFileShippingMarksXlsx(link.signedUrl, link.id, file)]
        }),
        catchError(error => of(new LoadShippingMarksXlsxFail(error)))
      )
    )
  );


	constructor(
		private actions$: Actions,
		private api: ApiService,
		private fs: FlashService,
		private ws: WebsocketService,
	) { }
}
